import { useState } from "react";
import Footer from "../components/Footer";
import referralBg from '../assets/images/referral-bg.png';

const BENEFITS = [
  {
    number: "01",
    title: "PERSONALIZED FOLLOW-UP",
    desc: "Every referral receives a dedicated consultation call — no generic sales funnels, no pressure.",
  },
  {
    number: "02",
    title: "TAILORED COVERAGE OPTIONS",
    desc: "Our advisors assess each referral's unique needs and present the best-fit policy.",
  },
  {
    number: "03",
    title: "FAST & TRANSPARENT PROCESS",
    desc: "From first contact to bound policy, we keep everyone in the loop — including you.",
  },
  {
    number: "04",
    title: "YOUR REPUTATION, PROTECTED",
    desc: "We represent your referral the same way we represent you — with care, precision, & no shortcuts.",
  },
];

export default function ReferralProgramPage() {
  const [form, setForm] = useState({
    referredName: "",
    referredEmail: "",
    referredPhone: "",
    yourName: "",
    yourEmail: "",
    notes: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [phoneError, setPhoneError] = useState("");

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handlePhoneChange = (e) => {
    const digits = e.target.value.replace(/\D/g, "").slice(0, 10);
    const formatted = digits.replace(
      /(\d{3})(\d{0,3})(\d{0,4})/,
      (_, a, b, c) =>
        c ? `(${a}) ${b}-${c}` : b ? `(${a}) ${b}` : a ? `(${a}` : ""
    );
    setForm({ ...form, referredPhone: formatted });
    if (digits.length > 0 && digits.length < 10) {
      setPhoneError("Please enter a valid 10-digit phone number.");
    } else {
      setPhoneError("");
    }
  };

  const handleSubmit = async () => {
    if (!form.referredName || !form.referredEmail || !form.referredPhone) {
      alert("Please fill in all required fields.");
      return;
    }
    if (form.referredPhone.replace(/\D/g, "").length < 10) {
      setPhoneError("Please enter a valid 10-digit phone number.");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/referrals`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          referredPhone: `+1 ${form.referredPhone}`,
        }),
      });
      if (!res.ok) throw new Error("Failed to submit referral.");
      setSubmitted(true);
    } catch (err) {
      alert(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full" style={{ backgroundColor: "#F7F4EF", minHeight: "100vh" }}>

      {/* Hero */}
      <div className="relative w-full flex flex-col items-center justify-center text-center py-24 px-6" style={{ minHeight: "340px" }}>
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${referralBg})` }}
        />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, rgba(1,46,114,0.72), rgba(0,0,0,0.55))" }} />
        <div className="relative z-10 max-w-2xl mx-auto">
          <div className="flex items-center justify-center gap-3 mb-5">
            <div className="h-px w-8 bg-white opacity-60" />
            <span className="font-cinzel uppercase tracking-[0.25em] text-white opacity-80" style={{ fontSize: "12px" }}>
              <span className="font-bold">Referral</span> Program
            </span>
            <div className="h-px w-8 bg-white opacity-60" />
          </div>
          <h1 className="font-cinzel font-bold text-white leading-[1.1] mb-5" style={{ fontSize: "clamp(32px, 5vw, 58px)" }}>
            Your Trust is Our
            <br />
            <em className="font-constantia italic font-normal">Greatest Asset</em>
          </h1>
          <p className="font-constantia text-white opacity-80 leading-relaxed" style={{ fontSize: "clamp(15px, 2vw, 18px)" }}>
            Know someone who needs dependable coverage?
            <br className="hidden sm:block" />
            Refer them to Paladin and let us take care of the rest.
          </p>
        </div>
      </div>

      {/* Body */}
      <div className="max-w-6xl mx-auto px-6 py-20 grid grid-cols-1 lg:grid-cols-[1fr_1.2fr] gap-12 items-center">

        {/* LEFT */}
        <div>
          <div className="flex items-center gap-3 mb-4">
            <div className="h-px w-7 bg-blue-900 opacity-40" />
            <span className="font-cinzel uppercase tracking-widest text-blue-900 opacity-60" style={{ fontSize: "11px" }}>
              Why <strong>Refer</strong>
            </span>
          </div>
          <h2 className="font-cinzel font-bold text-blue-900 leading-[1.15] mb-5" style={{ fontSize: "clamp(26px, 3.5vw, 40px)" }}>
            Built on <em className="font-constantia italic">Relationships</em>,
            <br />Not Transactions.
          </h2>
          <p className="font-constantia text-gray-600 leading-relaxed mb-4" style={{ fontSize: "16px" }}>
            At Paladin, every referral is a trust signal. When you send someone our way, we treat them with the same integrity and expertise that earned your confidence.
          </p>

          {/* Benefits */}
          <div className="flex flex-col gap-0">
            {BENEFITS.map((b, i) => (
              <div key={i}>
                <div className="flex items-start gap-5 py-6">
                  <div className="flex-shrink-0 w-10 h-10 rounded-md flex items-center justify-center font-cinzel font-bold text-white text-sm" style={{ backgroundColor: "#012E72" }}>
                    {b.number}
                  </div>
                  <div>
                    <p className="font-cinzel font-bold text-blue-900 mb-1 tracking-wide" style={{ fontSize: "13px" }}>{b.title}</p>
                    <p className="font-constantia text-gray-500 leading-relaxed" style={{ fontSize: "15px" }}>{b.desc}</p>
                  </div>
                </div>
                {i < BENEFITS.length - 1 && <div className="h-px w-full" style={{ background: "rgba(1,46,114,0.12)" }} />}
              </div>
            ))}
          </div>
        </div>

        {/* RIGHT — Form */}
        <div className="rounded-2xl overflow-hidden shadow-xl" style={{ backgroundColor: "#fff" }}>
          <div className="px-8 py-7" style={{ backgroundColor: "#012E72" }}>
            <h3 className="font-cinzel font-bold text-white leading-snug" style={{ fontSize: "clamp(22px, 3vw, 30px)" }}>
              Submit a <em className="font-constantia italic font-normal">Referral</em>
            </h3>
            <p className="font-constantia text-white opacity-70 mt-1" style={{ fontSize: "15px" }}>
              Fill in the details below. Our team will reach out within one business day.
            </p>
          </div>

          {submitted ? (
            <div className="px-8 py-16 flex flex-col items-center text-center gap-4">
              <div className="w-14 h-14 rounded-full flex items-center justify-center mb-2" style={{ backgroundColor: "#012E72" }}>
                <svg width="28" height="28" fill="none" stroke="white" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h4 className="font-cinzel font-bold text-blue-900 text-xl">Referral Submitted!</h4>
              <p className="font-constantia text-gray-500 leading-relaxed max-w-sm">
                Thank you for your referral. Our team will reach out to <strong>{form.referredName}</strong> within one business day.
              </p>
              <button
                onClick={() => { setSubmitted(false); setForm({ referredName: "", referredEmail: "", referredPhone: "", yourName: "", yourEmail: "", notes: "" }); setPhoneError(""); }}
                className="mt-4 font-cinzel text-sm uppercase tracking-widest text-blue-900 underline underline-offset-4 hover:opacity-70 transition-opacity"
              >
                Submit another referral
              </button>
            </div>
          ) : (
            <div className="px-8 py-8 flex flex-col gap-5">
              {/* Referred Name */}
              <div className="flex flex-col gap-1">
                <label className="font-cinzel text-blue-900 tracking-widest uppercase" style={{ fontSize: "11px" }}>
                  Referred Person's Full Name <span className="text-red-500">*</span>
                </label>
                <input name="referredName" value={form.referredName} onChange={handleChange} placeholder="Jane Smith"
                  className="w-full border rounded-md px-4 py-3 font-constantia text-gray-700 outline-none focus:ring-2 focus:ring-blue-900/30 transition"
                  style={{ fontSize: "15px", borderColor: "#e2e8f0" }} />
              </div>

              {/* Referred Email */}
              <div className="flex flex-col gap-1">
                <label className="font-cinzel text-blue-900 tracking-widest uppercase" style={{ fontSize: "11px" }}>
                  Referred Person's Email <span className="text-red-500">*</span>
                </label>
                <input name="referredEmail" value={form.referredEmail} onChange={handleChange} placeholder="jane@example.com" type="email"
                  className="w-full border rounded-md px-4 py-3 font-constantia text-gray-700 outline-none focus:ring-2 focus:ring-blue-900/30 transition"
                  style={{ fontSize: "15px", borderColor: "#e2e8f0" }} />
              </div>

              {/* Referred Phone */}
              <div className="flex flex-col gap-1">
                <label className="font-cinzel text-blue-900 tracking-widest uppercase" style={{ fontSize: "11px" }}>
                  Referred Person's Phone <span className="text-red-500">*</span>
                </label>
                <div
                  className="flex items-center border rounded-md overflow-hidden focus-within:ring-2 focus-within:ring-blue-900/30 transition"
                  style={{ borderColor: phoneError ? "#ef4444" : "#e2e8f0" }}
                >
                  <span
                    className="font-constantia text-gray-500 px-3 py-3 border-r flex-shrink-0"
                    style={{ fontSize: "15px", borderColor: "#e2e8f0", backgroundColor: "#f8fafc" }}
                  >
                    +1
                  </span>
                  <input
                    value={form.referredPhone}
                    onChange={handlePhoneChange}
                    placeholder="(555) 000-0000"
                    type="tel"
                    className="w-full px-4 py-3 font-constantia text-gray-700 outline-none"
                    style={{ fontSize: "15px" }}
                  />
                  {form.referredPhone.replace(/\D/g, "").length === 10 && (
                    <span className="pr-3 text-green-500 flex-shrink-0">
                      <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                      </svg>
                    </span>
                  )}
                </div>
                {phoneError && (
                  <p className="font-constantia text-red-400 mt-1" style={{ fontSize: "12px" }}>{phoneError}</p>
                )}
              </div>

              {/* Divider */}
              <div className="flex items-center gap-3 my-1">
                <div className="h-px flex-1" style={{ background: "rgba(1,46,114,0.12)" }} />
                <span className="font-cinzel text-blue-900 opacity-40 tracking-widest uppercase" style={{ fontSize: "10px" }}>Your Info (Optional)</span>
                <div className="h-px flex-1" style={{ background: "rgba(1,46,114,0.12)" }} />
              </div>

              {/* Your Name + Email */}
              <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col gap-1">
                  <label className="font-cinzel text-blue-900 tracking-widest uppercase" style={{ fontSize: "11px" }}>Your Name</label>
                  <input name="yourName" value={form.yourName} onChange={handleChange} placeholder="Your Name"
                    className="w-full border rounded-md px-4 py-3 font-constantia text-gray-700 outline-none focus:ring-2 focus:ring-blue-900/30 transition"
                    style={{ fontSize: "15px", borderColor: "#e2e8f0" }} />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="font-cinzel text-blue-900 tracking-widest uppercase" style={{ fontSize: "11px" }}>Your Email</label>
                  <input name="yourEmail" value={form.yourEmail} onChange={handleChange} placeholder="you@email.com" type="email"
                    className="w-full border rounded-md px-4 py-3 font-constantia text-gray-700 outline-none focus:ring-2 focus:ring-blue-900/30 transition"
                    style={{ fontSize: "15px", borderColor: "#e2e8f0" }} />
                </div>
              </div>

              {/* Notes */}
              <div className="flex flex-col gap-1">
                <label className="font-cinzel text-blue-900 tracking-widest uppercase" style={{ fontSize: "11px" }}>Notes / Coverage Interest</label>
                <input name="notes" value={form.notes} onChange={handleChange} placeholder="e.g. Home + Auto Mobile"
                  className="w-full border rounded-md px-4 py-3 font-constantia text-gray-700 outline-none focus:ring-2 focus:ring-blue-900/30 transition"
                  style={{ fontSize: "15px", borderColor: "#e2e8f0" }} />
              </div>

              {/* Submit */}
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="w-full py-4 font-cinzel font-bold uppercase tracking-widest text-white rounded-md transition-colors mt-2"
                style={{ backgroundColor: loading ? "#6b7280" : "#012E72", fontSize: "14px", cursor: loading ? "not-allowed" : "pointer" }}
              >
                {loading ? "Submitting..." : "Submit Referral"}
              </button>
              <p className="font-constantia text-center text-gray-400 leading-relaxed" style={{ fontSize: "13px" }}>
                All information is kept confidential and used solely for insurance consultation purposes.
              </p>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}